import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, ArrowLeft } from "lucide-react"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideTitle, hideMobileChrome, ...props }, ref) => {
  // Detecteer of een DialogTitle in children aanwezig is (voor a11y compliance)
  const hasTitle = React.Children.toArray(children).some((c) => {
    if (!React.isValidElement(c)) return false;
    if (c.type === DialogTitle || c.type?.displayName === "DialogTitle") return true;
    // Ook DialogHeader met DialogTitle er in
    if (c.type === DialogHeader || c.type?.displayName === "DialogHeader") {
      return React.Children.toArray(c.props?.children || []).some(
        (cc) => React.isValidElement(cc) && (cc.type === DialogTitle || cc.type?.displayName === "DialogTitle")
      );
    }
    return false;
  });

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: bottom-sheet stijl (slide from bottom, full-width, rounded top)
        "fixed z-50 grid gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "inset-x-0 bottom-0 max-h-[92vh] w-full rounded-t-2xl border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto overflow-x-hidden scroll-momentum [&>*]:min-w-0",
        "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        // Tablet/desktop: klassieke gecentreerde modal
        "sm:left-[50%] sm:right-auto sm:bottom-auto sm:top-[50%] sm:max-h-[90vh] sm:w-full sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border sm:p-6 sm:pb-6",
        "sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-top-[48%] sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95",
        className
      )}
      {...props}>
      {/* A11y-fallback: als geen DialogTitle is gegeven, injecteer visually-hidden titel */}
      {!hasTitle && (
        <VisuallyHidden.Root>
          <DialogPrimitive.Title>Dialoogvenster</DialogPrimitive.Title>
        </VisuallyHidden.Root>
      )}
      {/* Bottom-sheet grabber (alleen mobile) */}
      {!hideMobileChrome && (
        <div className="sm:hidden mx-auto -mt-2 mb-1 h-1.5 w-10 rounded-full bg-slate-300/80" aria-hidden="true" />
      )}
      {children}
      {/* Mobiele sticky terug-knop — automatisch op ALLE dialogen (< 640px) */}
      {!hideMobileChrome && (
        <div className="sm:hidden sticky -bottom-4 z-10 -mx-4 -mb-4 mt-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-white/95 backdrop-blur border-t border-border">
          <DialogPrimitive.Close asChild>
            <button
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-input bg-white py-2.5 text-sm font-medium text-slate-700 active:bg-slate-100"
              data-testid="dialog-terug-mobiel">
              <ArrowLeft className="h-4 w-4" /> Terug
            </button>
          </DialogPrimitive.Close>
        </div>
      )}
      <DialogPrimitive.Close
        className={cn("absolute right-3 top-3 sm:right-4 sm:top-4 rounded-full p-2.5 sm:p-1.5 opacity-80 ring-offset-background transition-opacity hover:opacity-100 bg-slate-100 sm:bg-transparent hover:bg-slate-200 sm:hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-20", hideMobileChrome && "max-sm:hidden")}
        data-testid="dialog-close-x">
        <X className="h-5 w-5 sm:h-4 sm:w-4" />
        <span className="sr-only">Sluiten</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
